import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Table, Form, Modal, Badge, Spinner, Alert, Pagination } from 'react-bootstrap';
import apiService from '../services/api';

const PlatformRoles = ({ organizationId = null, organizationName = null, onClose = null, onManagePermissions = null }) => {
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [saving, setSaving] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [permissionsRole, setPermissionsRole] = useState(null);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [permLoading, setPermLoading] = useState(false);
  const [permSaving, setPermSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    organizationId: organizationId || '',
    name: '',
    code: '',
    ordinal: '',
    is_active: true
  });

  useEffect(() => {
    loadRoles();
    if (!organizationId) {
      loadOrganizations();
    }
  }, [currentPage, search, itemsPerPage, organizationId]);

  const loadRoles = async () => {
    setLoading(true);
    setError('');

    try {
      let response;
      if (organizationId) {
        // Load roles for specific organization
        response = await apiService.getPlatformRolesByOrganization(organizationId, currentPage, itemsPerPage, search);
      } else {
        // Load all roles
        response = await apiService.getPlatformRoles(currentPage, itemsPerPage, search);
      }

      if (response.success) {
        setRoles(response.data || []);
        setTotalPages(Math.ceil((response.pagination?.total || 0) / itemsPerPage));
      } else {
        setError(response.error || 'Failed to load platform roles');
      }
    } catch (error) {
      console.error('Error loading platform roles:', error);
      setError('Failed to load platform roles');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    setLoadingOrganizations(true);

    try {
      const response = await apiService.getOrganizations(1, 1000); // Get all organizations
      if (response.success) {
        setOrganizations(response.data || []);
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoadingOrganizations(false);
    }
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' || e.type === 'blur') {
      setCurrentPage(1);
      setSearch(e.target.value);
    }
  };

  const handleShowModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        organizationId: role.organization_id || organizationId || '',
        name: role.name || '',
        code: role.code || '',
        ordinal: role.ordinal !== undefined && role.ordinal !== null ? role.ordinal : '',
        is_active: role.is_active !== undefined ? !!role.is_active : true
      });
    } else {
      setEditingRole(null);
      setFormData({
        organizationId: organizationId || '',
        name: '',
        code: '',
        ordinal: '',
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRole(null);
    setFormData({
      organizationId: organizationId || '',
      name: '',
      code: '',
      ordinal: '',
      is_active: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.organizationId) {
      setError('Please select an organization');
      return;
    }
    if (!formData.name.trim()) {
      setError('Role name is required');
      return;
    }
    if (!formData.code.trim()) {
      setError('Role code is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const roleData = {
        organization_id: parseInt(formData.organizationId),
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        ordinal: formData.ordinal === '' || formData.ordinal === null ? null : parseInt(formData.ordinal),
        is_active: !!formData.is_active
      };

      let response;
      if (editingRole) {
        response = await apiService.updatePlatformRole(editingRole.id, roleData);
      } else {
        response = await apiService.createPlatformRole(roleData);
      }

      if (response.success) {
        handleCloseModal();
        await loadRoles();
      } else {
        setError(response.error || 'Failed to save platform role');
      }
    } catch (error) {
      console.error('Error saving platform role:', error);
      setError('Failed to save platform role');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete the role "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiService.deletePlatformRole(id);
      if (response.success) {
        await loadRoles();
      } else {
        setError(response.error || 'Failed to delete platform role');
      }
    } catch (error) {
      console.error('Error deleting platform role:', error);
      setError('Failed to delete platform role');
    }
  };

  const handleManagePermissions = async (role) => {
    // If parent provided a handler, delegate to it
    if (typeof onManagePermissions === 'function') {
      return onManagePermissions(role);
    }
    if (typeof (window?.onManagePermissions) === 'function') {
      // backward compat hook if app attached a global
      return window.onManagePermissions(role);
    }

    // Load permissions and role-assigned permissions then open modal
    setPermLoading(true);
    setPermissionsRole(role);
    setSelectedPermissions([]);
    setAllPermissions([]);
    try {
      const [allResp, rolePermResp] = await Promise.all([
        // Prefer the role's organization_id when available; fall back to the component prop
        apiService.getAllPermissionsByOrganization(role.organization_id || organizationId),
        apiService.getPlatformRolePermissions(role.id)
      ]);
      console.log('Permissions loaded:', allResp)
      console.log('Role permissions loaded:', rolePermResp);

      // Normalize permission definitions into { id, code, name }
      if (allResp.success) {
        const normalizedAll = (allResp.data || []).map(p => ({
          id: p.platform_permission_id || p.platform_access_right_id || p.id || p.access_right_id || null,
          code: p.access_right_code || p.code || p.name || null,
          name: p.access_right_description || p.name || p.code || String(p.access_right_code || p.code || p.name || ''),
          // preserve module-level flag from various possible property names
          is_module_level: p.is_module_level || p.isModuleLevel || p.isModule || false
        }));
        // sort so module-level permissions appear first, then by code (case-insensitive)
        const sortedAll = normalizedAll.sort((a, b) => {
          if ((a.is_module_level || false) === (b.is_module_level || false)) {
            return String(a.code || '').localeCompare(String(b.code || ''), undefined, { sensitivity: 'base' });
          }
          return a.is_module_level ? -1 : 1;
        });
        setAllPermissions(sortedAll);
      }

      if (rolePermResp.success) {

        //console.log('Raw role permissions data:', rolePermResp.data);
        // rolePermResp.data may be an array of objects (joined rows) or ids/codes
        const assignedIds = (rolePermResp.data || []).map(p => {
          if (p == null) return null;
          if (typeof p === 'number') return p;
          if (typeof p === 'string') {
            // try to resolve string code to an id from normalized all permissions
            const found = (allResp.success ? (allResp.data || []) : []).find(a => (a.access_right_code || a.code || a.name) === p);
            if (found) return found.platform_access_right_id || found.id;
            return p;
          }
          // object case: prefer platform_permission_id, then platform_access_right_id, then access_right_code
          return p.platform_access_right_id || p.access_right_id || null;
        }).filter(x => x != null);

        //console.log('Normalized assigned permission IDs:', assignedIds);
        setSelectedPermissions(assignedIds);
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError('Failed to load permissions');
    } finally {
      setPermLoading(false);
      setShowPermissionsModal(true);
    }
  };

  const togglePermission = async (permKey) => {
    if (!permissionsRole) return;
    setPermSaving(true);
    setError('');

    // Resolve numeric id for the permission if a code was passed
    let permId = null;
    if (typeof permKey === 'number') {
      permId = permKey;
    } else {
      const found = (allPermissions || []).find(p => {
        const code = p.code || p.access_right_code || String(p.id || p.platform_access_right_id || p.platform_permission_id || '');
        const name = p.name || p.access_right_description || p.name;
        return code === permKey || name === permKey;
      });
      permId = found ? (found.id || found.platform_access_right_id || found.platform_permission_id || null) : null;
    }

    // If we couldn't resolve an id, show error
    if (permId == null) {
      window.alert('Unable to resolve permission id for this item.');
      setPermSaving(false);
      return;
    }

    const isSelected = selectedPermissions.includes(permId) || selectedPermissions.includes(permKey);

    try {
      if (!isSelected) {
        // Add permission via POST /platform_permission
        const payload = { platform_role_id: permissionsRole.id, platform_access_right_id: permId };
        await apiService.post('/platform_permission', payload);
        // Update local state after success
        setSelectedPermissions(prev => ([...prev, permId]));
        window.alert('Permission added');
      } else {
        // Remove permission via DELETE /platform_permission_from_role/:platform_role_id
        const endpoint = `/platform_permission_from_role/${permissionsRole.id}`;
        await apiService.delete(endpoint, { body: JSON.stringify({ platform_access_right_id: permId }) });
        setSelectedPermissions(prev => prev.filter(p => p !== permId));
        window.alert('Permission removed');
      }
      // refresh roles list to reflect any server-side changes
      await loadRoles();
    } catch (err) {
      console.error('Error toggling permission:', err);
      window.alert('Failed to update permission: ' + (err.message || err));
    } finally {
      setPermSaving(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!permissionsRole) return;
    setPermSaving(true);
    setError('');
    try {
      const response = await apiService.updatePlatformRolePermissions(permissionsRole.id, selectedPermissions);
      if (response.success) {
        setShowPermissionsModal(false);
        setPermissionsRole(null);
        await loadRoles();
      } else {
        setError(response.error || 'Failed to save role permissions');
      }
    } catch (err) {
      console.error('Error saving permissions:', err);
      setError('Failed to save role permissions');
    } finally {
      setPermSaving(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const getOrganizationName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org ? org.name : `Organization ${orgId}`;
  };

  return (
    <div>
      {/* Header */}
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-people me-2"></i>
              {organizationName ? `Platform Roles - ${organizationName}` : 'Platform Roles'}
            </h2>
            <div className="d-flex gap-2">
              <Button variant="primary" onClick={() => handleShowModal()}>
                <i className="bi bi-plus-circle me-2"></i>
                Add Role
              </Button>
              {onClose && (
                <Button variant="outline-secondary" onClick={onClose}>
                  <i className="bi bi-arrow-left me-2"></i>
                  Back
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Search and Filter */}
      <Row className="mb-4">
        <Col md={6}>
          <Form.Control
            type="text"
            placeholder="Search roles by name or code..."
            onKeyDown={handleSearch}
            onBlur={handleSearch}
          />
        </Col>
        <Col md={6} className="text-end">
          <Form.Select
            style={{ width: 'auto', display: 'inline-block' }}
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
          </Form.Select>
        </Col>
      </Row>

      {/* Roles Table */}
      <Row>
        <Col>
          <Card>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : roles.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-people display-1"></i>
                  <div className="mt-2">No platform roles found</div>
                  {organizationName && (
                    <div className="small">for {organizationName}</div>
                  )}
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Role Name</th>
                      <th>Role Code</th>
                      <th>Ordinal</th>
                      {!organizationId && <th>Organization</th>}
                      <th>Status</th>
                      <th>Created Date</th>
                      <th>Last Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.map((role) => (
                      <tr key={role.id}>
                        <td>
                          <div className="fw-medium">{role.name}</div>
                        </td>
                        <td>
                          <Badge bg="secondary">{role.code}</Badge>
                        </td>
                        <td>{role.ordinal}</td>
                        {!organizationId && (
                          <td>
                            <div className="fw-medium">
                              {getOrganizationName(role.organization_id)}
                            </div>
                          </td>
                        )}
                        <td>
                          <Badge bg={role.is_active ? 'success' : 'secondary'}>
                            {role.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>{apiService.formatDate(role.created_at || new Date())}</td>
                        <td>{role.last_updated_at ? apiService.formatDate(role.last_updated_at) : '-'}</td>
                        <td>
                          <div className="d-flex gap-1">

                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleShowModal(role)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleManagePermissions(role)}
                              title="Manage Permissions"
                            >
                              <i className="bi bi-shield-lock"></i>
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(role.id, role.name)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>

            {/* Pagination */}
            {totalPages > 1 && (
              <Card.Footer>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, roles.length)} of {roles.length} entries
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="md">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingRole ? 'Edit Platform Role' : 'Add Platform Role'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            {!organizationId && (
              <Form.Group className="mb-3">
                <Form.Label>Organization *</Form.Label>
                <Form.Select
                  name="organizationId"
                  value={formData.organizationId}
                  onChange={handleInputChange}
                  required
                  disabled={loadingOrganizations}
                >
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name} ({org.code})
                    </option>
                  ))}
                </Form.Select>
                {loadingOrganizations && (
                  <Form.Text className="text-muted">
                    <Spinner size="sm" className="me-1" />
                    Loading organizations...
                  </Form.Text>
                )}
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Role Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter role name (e.g., Administrator, User, Manager)"
                required
                maxLength={100}
              />
              <Form.Text className="text-muted">
                Maximum 100 characters. Must be unique within the organization.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Role Code *</Form.Label>
              <Form.Control
                type="text"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                placeholder="Enter role code (e.g., ADMIN, USER, MGR)"
                required
                maxLength={50}
                style={{ textTransform: 'uppercase' }}
              />
              <Form.Text className="text-muted">
                Maximum 50 characters. Will be converted to uppercase. Must be unique within the organization.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Ordinal</Form.Label>
              <Form.Control
                type="number"
                name="ordinal"
                value={formData.ordinal}
                onChange={handleInputChange}
                placeholder="Enter ordinal (e.g., 1, 2, 3)"
                min={0}
                step={1}
              />
              <Form.Text className="text-muted">
                Optional. Controls the display order of roles.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="role-is-active-switch"
                name="is_active"
                label="Active"
                checked={!!formData.is_active}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Inactive roles cannot be assigned to users.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Role'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      {/* Manage Permissions Modal */}
      <Modal show={showPermissionsModal} onHide={() => { setShowPermissionsModal(false); setPermissionsRole(null); }} dialogClassName="modal-fullwidth" style={{ '--bs-modal-width': '95vw' }}>
        <Modal.Header closeButton>
          <Modal.Title>
            {permissionsRole ? `Manage Permissions - ${permissionsRole.name}` : 'Manage Permissions'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {permLoading ? (
            <div className="text-center p-3">
              <Spinner animation="border" />
            </div>
          ) : (
            <div>
              {allPermissions.length === 0 ? (
                <div className="text-center py-3 text-muted">
                  <i className="bi bi-shield-lock display-4"></i>
                  <div className="mt-2">No permission definitions available.</div>
                </div>
              ) : (
                <div className="permissions-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.25rem 1rem', maxHeight: '60vh', overflowY: 'auto', fontSize: '0.85rem', wordBreak: 'break-word' }}>
                  {allPermissions.map((perm, i) => {
                    // perm is normalized as { id, code, name } or could be raw if not normalized
                    const id = perm && (perm.id || perm.platform_permission_id || perm.platform_access_right_id) || null;
                    const code = perm && (perm.code || perm.access_right_code || perm.code) || String(id || i);
                    const labelBase = (perm && (perm.name || perm.access_right_description || perm.name)) || code;
                    const moduleMarker = (perm && (perm.is_module_level || perm.isModuleLevel || perm.isModule)) ? ' : MODULE LEVEL' : '';
                    const label = `${labelBase}${moduleMarker}`;
                    const checked = id != null ? selectedPermissions.includes(id) : selectedPermissions.includes(code);
                    const uniqueKey = id != null ? `perm-${id}` : `${code}-${i}`;
                    const inputId = `perm-${uniqueKey}`;
                    //console.log('Rendering permission checkbox:', { uniqueKey, label, checked });
                    return (
                      <Form.Check
                        type="checkbox"
                        id={inputId}
                        key={uniqueKey}
                        label={label}
                        checked={checked}
                        onChange={() => togglePermission(id != null ? id : code)}
                        disabled={permSaving}
                        className="mb-2"
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowPermissionsModal(false); setPermissionsRole(null); }} disabled={permSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSavePermissions} disabled={permSaving}>
            {permSaving ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Permissions'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PlatformRoles;
